import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { streamCopilot, api } from "../lib/api";

const STARTERS = [
  "Why is scalp care becoming a standalone category in Japan?",
  "What humid-weather beauty trends should we launch in India next quarter?",
  "Risks of launching a milk toner in India?",
  "What should Meolaa test first in fragrance layering?",
];

export default function CopilotPanel({ open, onClose }) {
  const [sessionId] = useState(() => {
    const k = "bm_session";
    let s = localStorage.getItem(k);
    if (!s) { s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(k, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.copilotHistory(sessionId).then(h => {
        if (h.messages?.length) setMessages(h.messages);
      });
    }
    // Only re-run when panel opens; sessionId is mount-stable, messages.length intentionally not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      for await (const ev of streamCopilot(sessionId, msg)) {
        if (ev.type === "delta") {
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.content };
            return copy;
          });
        } else if (ev.type === "error") {
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: `[copilot error] ${ev.content}` };
            return copy;
          });
        }
      }
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <aside
        data-testid="copilot-panel"
        className={`fixed right-0 top-0 bottom-0 w-full max-w-[480px] bg-bm-surface border-l border-white/10 z-50 flex flex-col transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <header className="px-4 h-14 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-bm-amber" />
            <span className="font-heading font-bold tracking-tight">AI COPILOT</span>
            <span className="label-mono text-bm-dim">claude-4.5</span>
          </div>
          <button onClick={onClose} data-testid="copilot-close" className="text-bm-dim hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-3">
              <div className="text-sm text-bm-muted leading-relaxed">
                Ask the system anything about consumer signals, behaviour shifts, opportunities, or India localization. The copilot explains the why behind every recommendation.
              </div>
              <div className="space-y-1.5">
                {STARTERS.map(s => (
                  <button key={s} data-testid={`copilot-starter-${s.slice(0, 12)}`}
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs font-mono p-2 border border-white/10 hover:border-bm-cyan/50 hover:text-bm-cyan transition-colors text-bm-muted">
                    {">"} {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] p-3 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-white/5 border border-white/10 text-white"
                  : "bg-bm-elevated border-l-2 border-bm-cyan text-white/90 leading-relaxed"
              }`} data-testid={`copilot-msg-${i}`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-3 h-3 animate-spin text-bm-cyan" /> : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-2 bg-bm-elevated border border-white/10 focus-within:border-bm-cyan transition-colors px-3 py-2">
            <span className="font-mono text-xs text-bm-amber">{">"}</span>
            <input
              data-testid="copilot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="ask copilot..."
              disabled={streaming}
              className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-bm-dim"
            />
            <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="copilot-send"
              className="text-bm-amber hover:text-white disabled:opacity-40">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
